import { observer } from "mobx-react";
import { SingleValue } from "react-select";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { Input } from "../../../../../components/inputs/input/input";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import Card from "../../../../../components/layouts/card/card";
import ControlGroup from "../../../../../components/layouts/control-group/control-group";
import currentPortalStore from "../../../../../stores/current-portal-store";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";
import { RESOLUTION_TYPE } from "../../../../resolutions/constants";

interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    dataName?: string; /** used by Candu to target an element */
}

const refundLimitDDOptions = ():IOption[] => {
    const options = [];
    for (let i = 100; i < 210; i=i+10) {
        options.push({label: `${i}%`, value: i});
    }
    return options;
};

const StoreCreditSettingsCard: React.FC<IProps> = observer(({ onExpandClick, isExpanded, dataName }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

    const storeSetting = currentPortalStore.setting;
    const refundsCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.REFUNDS);
    const onChangeReturnCreditExpireType = (value: SingleValue<IOption>) => {
        storeSetting?.setReturnCreditsExpireType(String(value?.value));
    }

    const onChangeGiftCardsExpiration = (value: SingleValue<IOption>) => {
        storeSetting?.setGiftCardsExpireType(String(value?.value));
    }

    const expireTypeOptions = [
        { label: getTranslation('DAYS', 'dashboard-general'), value: 'Days' },
        { label: getTranslation('MONTHS', 'dashboard-general'), value: 'Months' },
        { label: getTranslation('YEARS', 'dashboard-general'), value: 'Years' },
        { label: getTranslation('NEVER', 'dashboard-general'), value: 'Never' },
    ]

    const returnCreditExpirationValue = expireTypeOptions.find(o => o.value === storeSetting?.returnCreditsExpireType);

    const giftCardsExpireTypeValue = expireTypeOptions.find(o => o.value === storeSetting?.giftCardsExpireType); 

    const selectedGiftCardRefundLimitOption = refundLimitDDOptions().find(option => option.value === storeSetting?.giftCardRefundLimitPercentage);
    const selectedStoreCreditsRefundLimitOption = refundLimitDDOptions().find(option => option.value === storeSetting?.storeCreditsRefundLimitPercentage);

	const refundResolutionCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.REFUND_RESOLUTION);

	const isStoreCreditsSupported = !!refundResolutionCapability.supportedRefundTypes?.includes(RESOLUTION_TYPE.Credits);
	const isGiftCardsSupported = !!refundResolutionCapability.supportedRefundTypes?.includes(RESOLUTION_TYPE.GiftCard);

	if (!isGiftCardsSupported && !isStoreCreditsSupported) {
		return null;
	}

    return <Card
        toolbar={{
            title: (
                <FontBody1 variant={FONT_VARIANT.BOLD}>
                    <DashTranslation translationKey="STORE_CREDIT_SETTINGS" scope={pageScope} />
                </FontBody1>
            )
        }}
        expandCallback={onExpandClick}
        isExpanded={isExpanded}
        dataName={dataName}
    >
        {isStoreCreditsSupported && (
            <>
                <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>{getTranslation('STORE_CREDITS', pageScope)}</FontBody2>
                <FormFieldLine
                    label={getTranslation('RETURN_CREDITS_EXPIRATION', pageScope)}
                    note={getTranslation('STORE_CREDIT_VALIDITY_DURATION', pageScope)}
                    dataName="return-credits-expiration"
                >
                    <ControlGroup>
                        <Input
                            value={storeSetting?.returnCreditsExpire}
                            onChange={storeSetting?.setReturnCreditsExpire}
                            disabled={storeSetting?.returnCreditsExpireType === 'Never'}
                            maxlength={4}
                        />
                        <SelectDropdown
                            options={expireTypeOptions}
                            value={returnCreditExpirationValue}
                            onChange={onChangeReturnCreditExpireType}
                            isSearchable={false}
                        />
                    </ControlGroup>
                </FormFieldLine>
                {refundsCapability.isStoreCreditsRefundLimitSupported && (
                    <FormFieldLine
                        label={getTranslation('REFUND_LIMIT_DD_LABEL', pageScope)}
                        note={getTranslation('STORE_CREDITS_REFUND_LIMIT_DD_NOTE', pageScope)}
                        tooltip={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('REFUND_LIMIT_DD_TOOLTIP', pageScope)}</FontBody2>}
                        dataName="store-credits-refund-limit"
                    >
                        <SelectDropdown
                            options={refundLimitDDOptions()}
                            value={selectedStoreCreditsRefundLimitOption}
                            onChange={v => storeSetting?.setStoreCreditsRefundLimit(v?.value as number)}
                            isSearchable={false}
                        />
                    </FormFieldLine>
                )}
            </>
        )}

	    {isGiftCardsSupported && (
			<>
				<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>{getTranslation('GIFT_CARD', pageScope)}</FontBody2>
				<FormFieldLine
					label={getTranslation('GIFT_CARDS_EXPIRATION', pageScope)}
					note={getTranslation('GIFT_CARDS_VALIDITY_DURATION', pageScope)}
					dataName="gift-cards-expiration"
				>
					<ControlGroup>
						<Input
							value={storeSetting?.giftCardsExpire}
							onChange={storeSetting?.setGiftCardsExpire}
							disabled={storeSetting?.giftCardsExpireType === 'Never'}
							maxlength={4}
						/>
						<SelectDropdown
							options={expireTypeOptions}
							value={giftCardsExpireTypeValue}
							onChange={onChangeGiftCardsExpiration}
							isSearchable={false}
						/>
					</ControlGroup>
				</FormFieldLine>
				{refundsCapability.isGiftCardRefundLimitSupported && (
					<FormFieldLine
						label={getTranslation('REFUND_LIMIT_DD_LABEL', pageScope)}
						note={getTranslation('GIFT_CARD_REFUND_LIMIT_DD_NOTE', pageScope)}
						tooltip={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('REFUND_LIMIT_DD_TOOLTIP', pageScope)}</FontBody2>}
						dataName="gift-card-refund-limit"
					>
						<SelectDropdown
							options={refundLimitDDOptions()}
							value={selectedGiftCardRefundLimitOption}
							onChange={v => storeSetting?.setGiftCardRefundLimit(v?.value as number)}
							isSearchable={false}
						/>
					</FormFieldLine>
				)}
			</>
	    )}
    </Card>
});

export default StoreCreditSettingsCard;
